import React, { useState, useEffect } from 'react';
import { usePermissions } from 'react-admin';
import { Paper } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { MaterialTable } from '~/components';
import { useGetAllAccounts } from '~/hooks/accounts';
import {
  validateRequired,
  validateUniqueByCustomRule,
} from '~/resources/solutions/components/Handbook/utils';
import { validateTag } from '~/utils/tags';
import { useTranslate } from 'react-admin';
import { l } from '../PartnerCreate';
import { useTenantCurrencies } from '~/hooks/tenant';
import AccountTableSingleSelect from '~/components/AccountTableSingleSelect/AccountTableSingleSelect';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Account } from '~/types';

export interface RoutingRule {
  routingCode: string;
  accountId: string;
  currency?: string;
  transactionTags: string[];
  tableData?: any;
  __rowId?: number;
}

export interface Props {
  partnerId: string;
  rules: RoutingRule[];
  onChange: (rules: RoutingRule[]) => Promise<any>;
}

const RoutingTable = (props: Props) => {
  const [rules, setRules] = useState<RoutingRule[]>(props.rules);

  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(`fields.${key}`));

  const allRuleAccountIds = rules.map((r) => r.accountId).filter(Boolean);

  const { data: accountsById, isLoading: isAccountsByIdLoading } = useGetAllAccounts(
    {
      resource: 'partners',
      filters: { ids: allRuleAccountIds },
    },
    { enabled: allRuleAccountIds.length > 0 }
  );

  const accountNumberById = (id: string) => accountsById?.find((a) => a.id === id)?.number || id;

  const { data: tenantCurrencies } = useTenantCurrencies();

  useEffect(() => {
    setRules(props.rules);
  }, [props.rules]);

  const validateTransactionTags = (rowData: RoutingRule) => {
    const tags = rowData?.transactionTags || [];
    const hasMatch = tags.find((tag) => {
      return !validateTag(tag);
    });
    if (hasMatch) {
      return t('tagValidationError');
    }
    return true;
  };

  const { permissions } = usePermissions();
  if (!permissions) return null;

  const validationFuncByRoutingCodeAndCurrency = validateUniqueByCustomRule(
    rules,
    (newRecord, tableRecord) =>
      newRecord.routingCode === tableRecord.routingCode &&
      // both records have currency and they match
      ((newRecord.currency &&
        tableRecord.currency &&
        newRecord.currency === tableRecord.currency) ||
        // both records have no currency
        (!newRecord.currency && !tableRecord.currency)),
    t('routingCodeAndCurrencyValidationError')
  );

  return (
    <MaterialTable
      columns={[
        {
          title: t('routingCode'),
          field: 'routingCode',
          required: true,
          validate: (currentRow: any) => {
            const requiredMessage = validateRequired('routingCode')(currentRow);
            return requiredMessage === true
              ? validationFuncByRoutingCodeAndCurrency(currentRow)
              : requiredMessage;
          },
          width: 120,
          cellStyle: {
            minWidth: 120,
          },
          initialEditValue: 'DEFAULT',
        },
        {
          title: t('currency'),
          field: 'currency',
          type: 'select',
          required: false,
          validate: validationFuncByRoutingCodeAndCurrency,
          render: (rowData: any) => rowData?.currency,
          allowEmpty: true,
          options: (tenantCurrencies || []).map((c) => ({ value: c.code, text: c.code })),
        },
        {
          title: t('accountNumber'),
          field: 'accountId',
          required: true,
          width: 200,
          cellStyle: {
            minWidth: 250,
          },
          validate: validateRequired('accountId'),
          render: (rowData: any) =>
            isAccountsByIdLoading ? (
              <Skeleton width={200} height={24} />
            ) : (
              accountNumberById(rowData.accountId)
            ),
          editComponent: (editProps: { value: string; onChange: (v: string) => void }) => (
            <RoutingAccountEditCell
              editProps={editProps}
              partnerId={props.partnerId}
              label={t('accountNumber')}
              initialAccount={accountsById?.find((a) => a.id === editProps.value)}
              // to match material table height
              className={classes.accountSelect}
            />
          ),
        },
        {
          title: t('transactionTags'),
          field: 'transactionTags',
          enum: {},
          width: 200,
          cellStyle: {
            minWidth: 250,
          },
          validate: validateTransactionTags,
          render: (rowData: RoutingRule) => {
            return (rowData.transactionTags || []).join(', ');
          },
        },
      ]}
      components={{
        Container: (props: any) => <Paper variant='outlined'>{props.children}</Paper>,
      }}
      data={rules}
      editable={
        permissions?.includes('partner.update') && {
          onRowAdd: (newRow: RoutingRule) => {
            if (newRow.currency === '') {
              delete newRow.currency;
            }
            const newRows: RoutingRule[] = [newRow, ...rules];
            return props.onChange(sanitize(newRows)).then(() => setRules(newRows));
          },
          onRowUpdate: (newRow: RoutingRule, oldRow: RoutingRule) => {
            if (newRow.currency === '') {
              delete newRow.currency;
            }
            const newRows: RoutingRule[] = [];
            rules.forEach((r) => {
              if (r === oldRow) {
                newRows.push(newRow);
              } else {
                newRows.push(r);
              }
            });
            return props.onChange(sanitize(newRows)).then(() => setRules(newRows));
          },
          onRowDelete: (oldRow: RoutingRule) => {
            const newRows = rules.filter((r) => r !== oldRow);
            return props.onChange(sanitize(newRows)).then(() => setRules(newRows));
          },
        }
      }
      options={{
        actionsColumnIndex: -1,
        emptyRowsWhenPaging: false,
        showTitle: false,
        paging: false,
        addRowPosition: 'first',
        search: false,
      }}
    />
  );
};

const RoutingAccountEditCell = ({
  editProps,
  partnerId,
  label,
  initialAccount,
  className,
}: {
  editProps: { value: string; onChange: (v: string) => void };
  partnerId: string;
  label: string;
  initialAccount?: Account;
  className?: string;
}) => {
  const [selectedAccount, setSelectedAccount] = useState<Account | undefined>(initialAccount);

  return (
    <AccountTableSingleSelect
      resource='partners'
      profileId={partnerId}
      label={label}
      selectedAccount={selectedAccount}
      value={selectedAccount?.number}
      onChange={(account) => {
        setSelectedAccount(account);
        editProps.onChange(account.id);
      }}
      required
      className={className}
    />
  );
};

const sanitize = (records: RoutingRule[]) => {
  // eslint-disable-next-line
  return records.map(({ tableData, __rowId, ...rest }) => rest);
};

const useStyles = makeStyles(() => ({
  accountSelect: {
    minWidth: 240,
    marginTop: 13,
    marginBottom: -4,
    '& input': {
      height: 21,
    },
  },
}));

export default RoutingTable;
